import apiClient from './apiClient';
import { NotificationTypes, showNotification } from '../components/Notifications';

export interface NotificationSettings {
  type: string;
  recipients: string[];
}

export interface TenantMetadata {
  sisIntegration?: string;
  clientId?: string;
}

/**
 * Updates notification settings for staff records
 */
export const updateNotificationSettings = async (
  notifications: NotificationSettings[],
  staffEmails: string[],
  isValidIntegration: boolean,
): Promise<NotificationSettings[]> => {
  if (!isValidIntegration) {
    console.error('Cannot update notification settings: Invalid integration');
    showNotification(
      NotificationTypes.error,
      'Invalid Integration',
      'Cannot update notification settings due to invalid integration.',
    );
    throw new Error('Invalid integration');
  }

  try {
    const updatedRecipients = notifications.reduce((acc, notification) => {
      acc[notification.type] = notification.recipients;
      return acc;
    }, {} as Record<string, string[]>);

    updatedRecipients.staffRecords = staffEmails;

    const { data } = await apiClient.patch('/data-ingest/sis/parameter-group/notifications', {
      recipients: updatedRecipients,
    });

    showNotification(
      NotificationTypes.success,
      'Email Recipients Updated',
      'Email recipients updated successfully.',
    );

    return data || [];
  } catch (error) {
    console.error('Error updating notification settings:', error);
    showNotification(
      NotificationTypes.error,
      'Error Updating Emails',
      'Failed to update staff records email recipients.',
    );
    throw error;
  }
};

/**
 * Fetches notification settings and tenant metadata from the server
 */
export const getNotificationSettings = async (): Promise<{
  notifications: NotificationSettings[];
  tenantData: TenantMetadata;
  staffEmails: string[];
}> => {
  try {
    const notificationPromise = apiClient.get('/data-ingest/sis/parameter-group/notifications');
    const tenantPromise = apiClient.get('/data-ingest/sis/tenant-meta');

    const [{ data: notificationData }, { data: tenantData }] = await Promise.all([notificationPromise, tenantPromise]);

    console.log('Notification data:', notificationData);
    console.log('Tenant data:', tenantData);

    // Extract staff emails from notifications
    const staffRecordNotification = notificationData.find((notification) => notification.type === 'staffRecords');
    const staffEmails = staffRecordNotification?.recipients || [];

    return {
      notifications: notificationData,
      tenantData,
      staffEmails,
    };
  } catch (error) {
    console.error('Error fetching notification settings:', error);
    throw error;
  }
};

/**
 * Updates tenant metadata when OneRoster integration is detected
 */
export const updateTenantMeta = async (
  isOneRosterIntegration: boolean,
  sisIntegration: string,
  clientId: string,
): Promise<void> => {
  try {
    if (isOneRosterIntegration && sisIntegration === 'psSis' && clientId === '') {
      await apiClient.patch('data-ingest/sis/tenant-meta', {
        sisIntegration: 'oneRoster',
        createIfNotExists: true,
      });
    }
  } catch (error) {
    console.error('Error updating tenant meta:', error);
    throw error;
  }
};
